// Tier-2 <RadioGroup> backed by AntD Radio.Group (FORT-182). Options are passed
// as data (label/value); `optionType="button"` renders a segmented control
// (Radio.Button), which is how the app uses radios. Single standalone radios are
// rare, so the group is the primitive. Colour comes from the token-sourced AntD
// global theme.
import React from 'react';
import { Radio as AntRadio } from 'antd';
import type { CommonProps } from './types';

export type RadioValue = string | number;

export interface RadioOption {
  label: React.ReactNode;
  value: RadioValue;
  disabled?: boolean;
}

export interface RadioGroupProps extends CommonProps {
  value?: RadioValue;
  defaultValue?: RadioValue;
  onChange?: (value: RadioValue) => void;
  options: RadioOption[];
  disabled?: boolean;
  optionType?: 'default' | 'button';
  /**
   * Names the group for screen readers, so its options aren't announced without context.
   *
   * Note this labels AntD's wrapper div, which carries no role — AntD's types don't accept
   * one. The options themselves are native radios sharing a `name`, which is what actually
   * groups them for assistive tech; this just gives that group a name.
   */
  'aria-label'?: string;
  // With optionType="button", 'solid' fills the selected segment with the primary
  // colour (vs 'outline', which only tints its text/border). Use 'solid' when the
  // selected option must read at a glance. Defaults to AntD's 'outline'.
  buttonStyle?: 'outline' | 'solid';
  id?: string;
}

export const RadioGroup: React.FC<RadioGroupProps> = ({
  value,
  defaultValue,
  onChange,
  options,
  disabled,
  optionType = 'default',
  buttonStyle = 'outline',
  id,
  className,
  style,
  'data-testid': dataTestId,
  'aria-label': ariaLabel,
}) => (
  <AntRadio.Group
    aria-label={ariaLabel}
    id={id}
    value={value}
    defaultValue={defaultValue}
    onChange={(e) => onChange?.(e.target.value)}
    options={options}
    disabled={disabled}
    optionType={optionType}
    buttonStyle={buttonStyle}
    className={className}
    style={style}
    data-testid={dataTestId}
  />
);
